import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft } from "react-icons/fa6";
import { FaArrowRight } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import pdf from "../../../../Assets/Images/icons/pdf.png";
import { RiImageAddLine } from "react-icons/ri";
import Pagination from "../../../../libs/Pagination";
import { addBook, bookSequence, deleteBook, getBooks, updateBook, swapBookWithPage } from "../../../../libs/api/bookApi";
import { AppContext } from "../../../../context/appContext";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";


export default function Books({ currentCategoryId = null }) {
  const { bookCategoriesDropdown, fetchBookCategoriesDropdown, t, formatNum, isRTL } = useContext(AppContext);
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [title, setTitle] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [isHidden, setIsHidden] = useState(false);
  const [errors, setErrors] = useState({ title: "", pdfFile: "", category: "", cover: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loadingAddForm, setLoadingAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);


  const bookSwipeOptions = [
    {
      key: "RTL",
      value: "rtl",
      label: "RTL"
    },
    {
      key: "LTR",
      value: "ltr",
      label: "LTR"
    }
  ]

  const [bookSwipe, setBookSwipe] = useState(bookSwipeOptions[0]);

  useEffect(() => {
    fetchBooks(true);
  }, [currentPage, limit, debouncedSearch, currentCategoryId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const fetchBooks = async (isLoading) => {
    setLoading(isLoading);
    try {
      const categoryIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getBooks(currentPage, limit, debouncedSearch, categoryIdFilter);
      if (response.status === 200) {
        // Add sequence number to each book for drag and drop
        const booksWithSequence = response.data.data.map((book, index) => ({
          ...book,
          sequence: book.sequence || index + 1
        }));
        setBooks(booksWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'title':
        if (!value || !value.toString().trim()) {
          error = t('validation.bookTitleRequired');
        }
        break;
      case 'pdfFile':
        if (!value) {
          error = t('validation.pdfFileRequired');
        }
        break;
      case 'category':
        // Category is now optional, no error needed
        break;
      case 'cover':
        if (!value) {
          error = t('validation.bookCoverRequired');
        } else if (value && value.size && value.size > 25 * 1024 * 1024) {
          error = t('validation.bookCoverSizeLimit');
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = (name, value) => {
    if (name === 'title') setTitle(value);
    if (name === 'pdfFile') setPdfFile(value);
    if (name === 'category') setCategory(value);
    if (name === 'cover') setCoverImage(value);
    // Validate field
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);
    newErrors.pdfFile = validateField('pdfFile', pdfFile);
    newErrors.cover = validateField('cover', coverImage);
    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append('title', title);
    if (category) {
      formData.append('categoryId', category);
    }
    formData.append('pdf', pdfFile);
    formData.append('direction', bookSwipe.value);
    formData.append('isHidden', isHidden ? "true" : "false");
    if (coverImage) {
      formData.append('cover', coverImage);
    }
    try {
      setLoadingAddForm(true);
      const response = await addBook(formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("book_modal").close();
        toast.success(response?.data?.message || t('messages.bookAddedSuccess'));
        fetchBooks(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleUpdate = async () => {
    const formData = new FormData();
    formData.append('title', title);
    if (category) {
      formData.append('categoryId', category);
    }
    formData.append('pdf', pdfFile);
    formData.append('direction', bookSwipe.value);
    formData.append('isHidden', isHidden ? "true" : "false");
    if (coverImage) {
      formData.append('cover', coverImage);
    }
    try {
      setLoadingAddForm(true);
      const response = await updateBook(selectedBookId, formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("book_modal").close();
        toast.success(response?.data?.message || t('messages.bookUpdatedSuccess'));
        fetchBooks(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  }

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert
    const result = await Swal.fire({
      title: t('dialogs.areYouSure'),
      text: t('dialogs.cantRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('dialogs.yesDelete'),
      cancelButtonText: t('dialogs.cancel')
    });

    if (!result.isConfirmed) {
      return; // User cancelled the deletion
    }

    try {
      const response = await deleteBook(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || t('messages.bookDeletedSuccess'));
        fetchBooks(false);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (mode, book = null) => {
    // Refresh book categories dropdown to get latest categories
    fetchBookCategoriesDropdown();

    setModalMode(mode);
    setSelectedBookId(null);
    if (mode === "edit" && book) {
      setTitle(book?.title || null);
      setPdfFile(book?.pdf || null);
      setSelectedBookId(book?._id || null);
      setCategory(book?.categoryId?._id || null);
      setCoverImage(book?.cover || null);
      setBookSwipe(bookSwipeOptions.find(option => option.value === book.direction));
      setIsHidden(book?.isHidden || false);
      setErrors({ title: "", pdfFile: "", category: "", cover: "" });
    } else {
      setTitle("");
      setPdfFile(null);
      setSelectedBookId(null);
      setCategory("");
      setCoverImage(null);
      setBookSwipe(bookSwipeOptions[0]);
      setIsHidden(false);
      setErrors({ title: "", pdfFile: "", category: "", cover: "" });
    }

    document.getElementById("book_modal").showModal();
  };

  const getFileNameFromUrl = (url) => {
    return url.substring(url.lastIndexOf('/') + 1);
  };

  const clearForm = () => {
    setTitle("");
    setPdfFile(null);
    setSelectedBookId(null);
    setCategory("");
    setCoverImage(null);
    setIsHidden(false);
    setErrors({ title: "", pdfFile: "", category: "", cover: "" });
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFileType = fileName.endsWith('.pdf') || fileName.endsWith('.mp3');
      if (isValidFileType) {
        setPdfFile(file);
        // Clear any existing PDF file error since we have a valid file
        setErrors((prevErrors) => ({ ...prevErrors, pdfFile: '' }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, pdfFile: t('validation.invalidFileType') }));
      }
    }
  };

    const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedItem(books[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);
    
    // Create a custom drag image that only shows the dragged item
    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);
    
    // Style the drag image to look like a single item
    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';
    
    // Hide the original element during drag
    draggedElement.style.opacity = '0.3';
    
    // Set the custom drag image
    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(books[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = books[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedBooks = books.map(book => {
      if (book._id === draggedItem._id) {
        return { ...book, sequence: targetSequence };
      }
      if (book._id === draggedOverItem._id) {
        return { ...book, sequence: draggedSequence };
      }
      return book;
    });

    // Sort by sequence to reflect the swap
    updatedBooks.sort((a, b) => a.sequence - b.sequence);

    setBooks(updatedBooks);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);

    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedUpdateSequence'));
      // Revert if update fails
      fetchBooks(false);
    }
  };

  const handleDragEnd = (e) => {
    // Restore the original element's opacity
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await bookSequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchBooks(false);
        // toast.success(response?.data?.message || "Book sequence updated successfully");
      } else {
        // toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handleSwapWithPage = async (bookId, direction) => {
    try {
      const payload = {
        id: bookId,
        direction: direction,
        currentPage: currentPage,
        limit: limit
      };
      const response = await swapBookWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.bookMovedSuccessfully'));
        fetchBooks(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };


  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('book.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Book */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2  border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('book.addBook')}
          </button>
        </div>

        {/* Table */}
        {loading && <div className="flex justify-center items-center h-full p-6"><LoadingSpinner /></div>}
        {!loading && books.length === 0 && (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('book.noBooks')}</p>
          </div>
        )}
        {!loading && books.length > 0 && (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-5 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('common.id')}</div>
              <div className="p-2 py-4">{t('book.bookTitle')}</div>
              <div className="p-2 py-4">{t('book.bookCategory')}</div>
              <div className="p-2 py-4">{t('forms.direction')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>
            
            {/* Draggable Content */}
            <div>
              {books.map((book, index) => (
                <div
                  key={book._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-5 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === book._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${
                    dragOverItem?._id === book._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                  }`}
                  style={{
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    userSelect: 'none'
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {formatNum((currentPage - 1) * limit + index + 1)}
                  </div>
                  <div className="p-2 py-6 flex items-center gap-2">
                    {book?.title || "-"}
                    {book?.isHidden && (<span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>)}
                  </div>
                  <div className="p-2 py-6">{book?.categoryId?.title || "-"}</div>
                  <div className="p-2 py-6">{book?.direction === "rtl" ? t('forms.rtl') : t('forms.ltr') || "-"}</div>
                  <div className="p-2 py-6">
                    <div className="flex gap-3 items-center">
                      {index === 0 && currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(book._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {index === limit - 1 && currentPage < totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(book._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", book)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(book._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && books.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')} {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog
        id="book_modal"
        className="modal shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        {" "}
        <div className="modal-box min-w-[800px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('book.addBook') : t('book.editBook')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Category */}
            <label className="block text-sm font-medium mb-2">
              {t('book.bookCategory')}
            </label>
            <select
              value={category}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              className=" w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('forms.selectCategory')}</option>
              {bookCategoriesDropdown?.data?.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.displayTitle || cat.title}
                </option>
              ))}
            </select>
            {errors.category && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.category}</p>}

            {/* Book Cover Dropzone */}
            <div className="my-5 mt-1">
              <label className="block text-sm font-medium mb-2">
                {t('book.bookCover')}
              </label>
              <div
                className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith("image/")) {
                    if (file.size <= 25 * 1024 * 1024) {
                      setCoverImage(file);
                      handleFieldChange('cover', file);
                      // Clear any existing cover error
                      setErrors((prevErrors) => ({ ...prevErrors, cover: '' }));
                    } else {
                      setErrors((prevErrors) => ({ ...prevErrors, cover: t('validation.bookCoverSizeLimit') }));
                    }
                  }
                }}
                onClick={() =>
                  document.getElementById("coverUploadInput").click()
                }
              >
                {coverImage ? (
                  <img
                    src={typeof coverImage === 'string' ? coverImage : URL.createObjectURL(coverImage)}
                    alt="cover"
                    className="max-h-40 mx-auto mb-2"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                    <div className="bg-gray-100 rounded-full min-h-12  min-w-12 flex items-center justify-center">
                      <RiImageAddLine size={25} className="text-blue-500 " />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-md text-blue-500 font-medium">
                        {t('forms.clickToUpload')}
                      </p>
                      <p className="text-md text-black">{t('forms.orDragDrop')}</p>
                    </div>
                    <p className="text-sm text-black">
                      {t('forms.maxFileSize25MB')}
                    </p>
                  </div>
                )}
                <input
                  id="coverUploadInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size <= 25 * 1024 * 1024) {
                        setCoverImage(file);
                        handleFieldChange('cover', file);
                        // Clear any existing cover error
                        setErrors((prevErrors) => ({ ...prevErrors, cover: '' }));
                      } else {
                        setErrors((prevErrors) => ({ ...prevErrors, cover: t('validation.bookCoverSizeLimit') }));
                      }
                    }
                    // Clear the input value to allow selecting the same file again
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
            {errors.cover && <p className="text-red-500 absolute -mt-[18px] text-xs">{errors.cover}</p>}

            {/* Title */}
            <label className="block text-sm font-medium mt-6 mb-2">{t('book.bookTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder={t('forms.enterTitle')}
              className=" w-full mb-6 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.title && <p className="text-red-500 absolute -mt-[22px] text-xs">{errors.title}</p>}

            {/* Book Swipe */}
            <label className="block text-sm font-medium mb-2">{t('forms.direction')}</label>
            <select
              value={bookSwipe.value}
              onChange={(e) => setBookSwipe(bookSwipeOptions.find(option => option.value === e.target.value))}
              className=" w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {bookSwipeOptions.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* File Upload */}
            <div className="flex flex-col-reverse items-baseline gap-5 mt-2">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <IoMdAdd size={22} />
                <span className="text-sm font-medium">{t('forms.attachFile')}</span>
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e);
                    // Clear the input value to allow selecting the same file again
                    e.target.value = '';
                  }}
                />
              </label>
              {pdfFile && (
                <span className="text-sm flex items-center gap-2">
                  <img src={pdf} alt="pdf" />
                  {typeof pdfFile === 'string' ? getFileNameFromUrl(pdfFile) : pdfFile.name}
                </span>
              )}
            </div>
            {errors.pdfFile && <p className="text-red-500 absolute mt-[2px] text-xs">{errors.pdfFile}</p>}

            {/* Hidden */}
            <div className="flex items-center gap-2 mt-8">
              <input type="checkbox" id="book_isHidden" checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)} className="checkbox checkbox-sm" />
              <label htmlFor="book_isHidden" className="text-sm font-medium cursor-pointer">{t('forms.hidden')}</label>
            </div>
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loadingAddForm || loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loadingAddForm ? <LoadingSpinner /> : modalMode === "add" ? t('common.add') : t('common.update')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}